import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Chip, Tooltip, IconButton } from '@mui/material';
import { AccessTime as TimeIcon, TimerOff as TimerOffIcon, VolumeUp, VolumeOff } from '@mui/icons-material';
import { differenceInSeconds, formatDuration, intervalToDuration, isPast, isFuture, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const ProjectCountdown = ({ endDate, startDate, status: projectStatus, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, active, not_started, expired, invalid, archived
  const [isMuted, setIsMuted] = useState(true);
  const audioCtxRef = useRef(null);
  const nextTickRef = useRef(0);

  const initAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (isMuted) {
      initAudio();
    }
    setIsMuted(!isMuted);
  };

  const playTick = () => {
    if (isMuted || !audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.05);
  };

  const playHeartbeat = () => {
    if (isMuted || !audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    const t = ctx.currentTime;

    // Lub
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(60, t);
    gain1.gain.setValueAtTime(0.2, t);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc1.start(t);
    osc1.stop(t + 0.15);

    // Dub
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(50, t + 0.2);
    gain2.gain.setValueAtTime(0.15, t + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc2.start(t + 0.2);
    osc2.stop(t + 0.4);
  };

  useEffect(() => {
    if (!endDate) {
      setStatus('invalid');
      return;
    }

    if (projectStatus === 'archived') {
      setStatus('archived');
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      // Parse dates in local time context
      const now = new Date();
      
      // Parse YYYY-MM-DD as local midnight
      let end = null;
      if (endDate) {
        const datePart = endDate.includes('T') ? endDate.split('T')[0] : endDate;
        const [year, month, day] = datePart.split('-').map(Number);
        end = new Date(year, month - 1, day);
        // Set to end of day (23:59:59) for countdown logic
        end.setHours(23, 59, 59, 999);
      }

      let start = null;
      if (startDate) {
        const datePart = startDate.includes('T') ? startDate.split('T')[0] : startDate;
        const [year, month, day] = datePart.split('-').map(Number);
        start = new Date(year, month - 1, day);
        // Set to start of day (00:00:00)
        start.setHours(0, 0, 0, 0);
      }
      
      if (!end || !isValid(end)) {
        setStatus('invalid');
        return;
      }

      // Check if project hasn't started yet
      if (start && isFuture(start)) {
        setStatus('not_started');
        const duration = intervalToDuration({ start: now, end: start });
        const totalDays = Math.floor((start - now) / (1000 * 60 * 60 * 24));
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, ...duration, totalDays });
        return;
      }

      // Check if project is expired
      if (isPast(end)) {
        setStatus('expired');
        const duration = intervalToDuration({ start: end, end: now });
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, ...duration });
        return;
      }

      // Project is active
      setStatus('active');
      const duration = intervalToDuration({ start: now, end: end });
      const totalDays = Math.floor((end - now) / (1000 * 60 * 60 * 24));
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0, ...duration, totalDays });

      // Handle sound effects
      const nowTime = Date.now();
      if (!isMuted && nowTime >= nextTickRef.current) {
        // Less than 24 hours left?
        if (totalDays < 1) {
          playHeartbeat();
          nextTickRef.current = nowTime + 1000; // Beat every second (approx 60bpm)
        } else {
          playTick();
          nextTickRef.current = nowTime + 1000;
        }
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate, startDate, isMuted]);

  if (status === 'invalid' || !timeLeft) return null;

  const formatDigit = (num) => String(num).padStart(2, '0');

  const renderContent = () => {
    if (status === 'loading') {
      return <Chip label="..." size="small" />;
    }

    if (status === 'invalid') {
      return null;
    }
    
    if (status === 'archived') {
      return (
        <Tooltip title="Le projet est archivé">
          <Chip
            icon={<TimerOffIcon />}
            label={compact ? "Archivé" : "Chrono arrêté (Archivé)"}
            color="default"
            size="small"
            variant="outlined"
          />
        </Tooltip>
      );
    }

    if (status === 'expired') {
      return (
        <Chip
          icon={<TimerOffIcon />}
          label={compact ? "Terminé" : "Délai dépassé"}
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }

    if (status === 'not_started') {
      return (
        <Tooltip title="Le projet n'a pas encore commencé">
          <Chip
            icon={<TimeIcon />}
            label={`Démarre dans ${timeLeft.totalDays}j`}
            color="default"
            size="small"
            variant="outlined"
          />
        </Tooltip>
      );
    }

    // Active status
    const isUrgent = timeLeft.totalDays < 3;
    const isCritical = timeLeft.totalDays < 1;
    
    let label = '';
    if (timeLeft.totalDays > 0) {
      label = `${timeLeft.totalDays}j ${timeLeft.hours}h`;
    } else {
      label = `${formatDigit(timeLeft.hours)}:${formatDigit(timeLeft.minutes)}:${formatDigit(timeLeft.seconds)}`;
    }

    if (compact) {
      return (
        <Tooltip title={`Temps restant: ${timeLeft.totalDays}j ${timeLeft.hours}h ${timeLeft.minutes}m`}>
          <Chip
            icon={<TimeIcon sx={{ animation: isCritical ? 'pulse 1s infinite' : 'none' }} />}
            label={label}
            color={isUrgent ? "error" : "primary"}
            size="small"
            variant={isUrgent ? "filled" : "outlined"}
            sx={{
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              }
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
          Temps restant
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={isUrgent ? "error.main" : "primary.main"}>
              {timeLeft.totalDays}
            </Typography>
            <Typography variant="caption" color="text.secondary">Jours</Typography>
          </Box>
          <Typography variant="h4" color="text.secondary">:</Typography>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={isUrgent ? "error.main" : "primary.main"}>
              {formatDigit(timeLeft.hours)}
            </Typography>
            <Typography variant="caption" color="text.secondary">Heures</Typography>
          </Box>
          <Typography variant="h4" color="text.secondary">:</Typography>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={isUrgent ? "error.main" : "primary.main"}>
              {formatDigit(timeLeft.minutes)}
            </Typography>
            <Typography variant="caption" color="text.secondary">Min</Typography>
          </Box>
          <Typography variant="h4" color="text.secondary">:</Typography>
          <Box>
            <Typography variant="h4" fontWeight="bold" color={isUrgent ? "error.main" : "primary.main"}>
              {formatDigit(timeLeft.seconds)}
            </Typography>
            <Typography variant="caption" color="text.secondary">Sec</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {renderContent()}
      {status === 'active' && (
        <IconButton size="small" onClick={toggleMute} color={isMuted ? "default" : "primary"}>
          {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );
};

export default ProjectCountdown;
